import { useEffect, useRef, useState } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { motion } from 'framer-motion'
import { AppDialog } from '../../Common/AppDialog'
import { IconButton } from '../../Common/IconButton'
import { StatusChip } from '../../Common'
import type { ChartDescriptor, ChartRecord } from '../charts'
import { useNls } from '../../../nls/nlsProvider'
import { ChartCanvasPanel } from './panels/ChartCanvasPanel'
import { CollapsibleHandle } from './panels/CollapsibleHandle'
import { ExportSharePanel } from './panels/ExportSharePanel'
import type { Workspace } from '../../../context/WorkspaceContext'
import type { AnalysisViewMode } from '../../../types/analysis'
import { RecordListPanel } from '../../Common'
import { useFocusTrap } from '../../../utils/useFocusTrap'

const RECORDS_STORAGE_KEY = 'analysis.chartPopover.recordListCollapsed'
const EXPORT_STORAGE_KEY = 'analysis.chartPopover.exportBarCollapsed'

const readStoredBoolean = (key: string, fallback: boolean) => {
  try {
    const value = window.localStorage.getItem(key)
    if (value === '1') return true
    if (value === '0') return false
  } catch {
    return fallback
  }
  return fallback
}

const writeStoredBoolean = (key: string, value: boolean) => {
  try {
    window.localStorage.setItem(key, value ? '1' : '0')
  } catch {
    /* localStorage can be unavailable in private or embedded contexts. */
  }
}

export const ChartPopoverDialog = ({
  descriptor,
  onClose,
  viewMode = 'LIVE',
  workspace,
  snapshotId,
  isReducedViewer = false,
}: {
  descriptor: ChartDescriptor | null
  onClose: () => void
  viewMode?: AnalysisViewMode
  workspace?: Workspace
  snapshotId?: string
  isReducedViewer?: boolean
}) => {
  const { t } = useNls()
  const canvasShellRef = useRef<HTMLDivElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  useFocusTrap(Boolean(descriptor), dialogRef)
  const [records, setRecords] = useState<ChartRecord[]>([])
  const [loadingRecords, setLoadingRecords] = useState(false)
  const [recordsCollapsed, setRecordsCollapsed] = useState(() => readStoredBoolean(RECORDS_STORAGE_KEY, false))
  const [exportCollapsed, setExportCollapsed] = useState(() => readStoredBoolean(EXPORT_STORAGE_KEY, false))

  useEffect(() => {
    if (!descriptor) return undefined
    let cancelled = false
    setLoadingRecords(true)
    void Promise.resolve(descriptor.buildRecords()).then((nextRecords) => {
      if (cancelled) return
      setRecords(nextRecords)
      setLoadingRecords(false)
    }, () => {
      if (cancelled) return
      setRecords([])
      setLoadingRecords(false)
    })
    return () => {
      cancelled = true
    }
  }, [descriptor])

  const toggleRecords = () => {
    setRecordsCollapsed((value) => {
      const next = !value
      writeStoredBoolean(RECORDS_STORAGE_KEY, next)
      return next
    })
  }

  const toggleExport = () => {
    setExportCollapsed((value) => {
      const next = !value
      writeStoredBoolean(EXPORT_STORAGE_KEY, next)
      return next
    })
  }

  if (!descriptor) return null

  const message = (
    <div
      className={`analysisChartDialog${recordsCollapsed ? ' isRecordsCollapsed' : ''}${exportCollapsed ? ' isExportCollapsed' : ''}`}
      data-chart-kind={descriptor.kind}
    >
      <header className="analysisChartDialog__header">
        <div>
          <StatusChip tone="neutral" size="sm">{descriptor.source.tab} · {descriptor.source.cardLabel}</StatusChip>
          <h2 id="analysis-chart-dialog-title">{descriptor.title}</h2>
          {descriptor.subtitle && <p>{descriptor.subtitle} · Live · local</p>}
        </div>
        <IconButton
          className="analysisChartDialog__close"
          variant="tertiary"
          size="sm"
          onClick={onClose}
          label={t('NLSAnalysisChartActionClose')}
          title={t('NLSAnalysisChartActionClose')}
          icon={<CloseRoundedIcon fontSize="inherit" />}
        />
      </header>
      <motion.div className="analysisChartDialog__main" layout>
        <div className="analysisChartDialog__canvasShell" ref={canvasShellRef}>
          <ChartCanvasPanel descriptor={descriptor} />
        </div>
        <aside className="analysisChartDialog__recordsShell">
          <CollapsibleHandle
            collapsed={recordsCollapsed}
            axis="horizontal"
            controls="analysis-chart-records"
            collapseLabel={t('NLSAnalysisChartCollapseRecords')}
            expandLabel={t('NLSAnalysisChartExpandRecords')}
            onToggle={toggleRecords}
          />
          {!recordsCollapsed && (
            <RecordListPanel
              records={records}
              loading={loadingRecords}
              onDrillRecord={(record) => {
                onClose()
                descriptor.onDrillRecord?.(record)
              }}
            />
          )}
        </aside>
      </motion.div>
      <motion.footer className="analysisChartDialog__exportShell" layout>
        <CollapsibleHandle
          collapsed={exportCollapsed}
          axis="vertical"
          controls="analysis-chart-export"
          collapseLabel={t('NLSAnalysisChartCollapseExport')}
          expandLabel={t('NLSAnalysisChartExpandExport')}
          onToggle={toggleExport}
        />
        <div id="analysis-chart-export">
          <ExportSharePanel
            collapsed={exportCollapsed}
            chartId={descriptor.id}
            descriptor={descriptor}
            records={records}
            loadingRecords={loadingRecords}
            viewMode={viewMode}
            workspace={workspace}
            snapshotId={snapshotId}
            isReducedViewer={isReducedViewer}
            getSvg={() => canvasShellRef.current?.querySelector('svg') ?? null}
            onOpenReports={() => {
              onClose()
              descriptor.onOpenReports?.()
            }}
          />
        </div>
      </motion.footer>
      <span className="analysisChartDialog__keyboardHint"><KeyboardArrowDownRoundedIcon fontSize="small" /> {t('NLSAnalysisChartKeyboardHint')}</span>
    </div>
  )

  return (
    <AppDialog
      open
      title={descriptor.title}
      size="analysisChart"
      message={message}
      actions={[]}
      onDismiss={onClose}
      dialogRef={dialogRef}
    />
  )
}
